import React from 'react';
import { TableColumn } from 'react-data-table-component';
import DefaultDataTable from '../../../../components/default/DefaultDataTable';
import {
    TransportationQuoteDetail,
    TransportationQuoteDetailFilters
} from '../../../../app/models/TransportationQuoteDetail';
import { momentParseDate } from '../../../../helpers';
import { formatMoney, formatNumber } from '../../../../utils/utils';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';

interface Props {
    quoteDetails: TransportationQuoteDetail[];
    callbackSuccess?: () => void;
    loading?: boolean;
    totalRows: number;
    edit?: (quoteDetailId: number) => void;
    destroy?: (quoteDetailId: number) => void;
    show?: (quoteDetailId: number) => void;
    filter: TransportationQuoteDetailFilters;
    setFilter: (filter: TransportationQuoteDetailFilters) => void;
    paginationRowsPerPageOptions?: number[];
}

const QuoteDetailsDataTable = ({
    quoteDetails,
    totalRows,
    filter,
    setFilter,
    loading = false,
    edit,
    destroy,
    show
}: Props) => {
    const getPlaceName = (place: TransportationQuoteDetail['origin']): string => {
        if (!place) {
            return '-';
        }

        if (typeof place === 'string') {
            return place;
        }

        return place.name ?? '-';
    };

    const getCurrencyLabel = (currency: TransportationQuoteDetail['currency']): string => {
        if (!currency) {
            return '-';
        }

        if (typeof currency === 'string') {
            return currency;
        }

        return currency.name ?? '-';
    };

    const parseNumber = (value: unknown): number => {
        if (value === null || value === undefined || value === '') {
            return 0;
        }

        const numericValue = typeof value === 'string' ? parseFloat(value) : Number(value);

        return Number.isNaN(numericValue) ? 0 : numericValue;
    };

    const columns: TableColumn<TransportationQuoteDetail>[] = [
        {
            name: 'Posición',
            selector: (row: TransportationQuoteDetail) => row.position ?? row.postion ?? '',
            sortable: true,
            sortField: 'position',
            minWidth: '90px',
            cell: (row: TransportationQuoteDetail) => (
                <span className="">{row.position ?? row.postion ?? '-'}</span>
            )
        },
        {
            name: 'Producto',
            selector: (row: TransportationQuoteDetail) => row.product_name ?? '',
            sortable: true,
            sortField: 'product_name',
            minWidth: '180px',
            cell: (row: TransportationQuoteDetail) => <span className="">{row.product_name ?? '-'}</span>
        },
        {
            name: 'Servicio',
            selector: (row: TransportationQuoteDetail) => row.service ?? '',
            sortable: true,
            sortField: 'service',
            minWidth: '200px',
            cell: (row: TransportationQuoteDetail) => <span className="">{row.service ?? '-'}</span>
        },
        {
            name: 'Tramo',
            selector: (row: TransportationQuoteDetail) => row.route_segment_id ?? '',
            sortable: true,
            sortField: 'route_segment_id',
            minWidth: '90px',
            cell: (row: TransportationQuoteDetail) => <span className="">{row.route_segment_id ?? '-'}</span>
        },
        {
            name: 'Origen',
            selector: (row: TransportationQuoteDetail) => getPlaceName(row.origin),
            sortable: true,
            sortField: 'origin',
            minWidth: '260px',
            cell: (row: TransportationQuoteDetail) => <span className="">{getPlaceName(row.origin)}</span>
        },
        {
            name: 'Destino',
            selector: (row: TransportationQuoteDetail) => getPlaceName(row.destiny),
            sortable: true,
            sortField: 'destiny',
            minWidth: '320px',
            cell: (row: TransportationQuoteDetail) => <span className="">{getPlaceName(row.destiny)}</span>
        },
        {
            name: 'Cantidad',
            selector: (row: TransportationQuoteDetail) => parseNumber(row.quantity),
            sortable: true,
            sortField: 'quantity',
            minWidth: '110px',
            cell: (row: TransportationQuoteDetail) => (
                <span className="">{formatNumber(parseNumber(row.quantity))}</span>
            )
        },
        {
            name: 'Unidad',
            selector: (row: TransportationQuoteDetail) => row.product_unit ?? '',
            sortable: true,
            sortField: 'product_unit',
            minWidth: '90px',
            cell: (row: TransportationQuoteDetail) => <span className="">{row.product_unit ?? '-'}</span>
        },
        {
            name: 'Tarifa de venta',
            selector: (row: TransportationQuoteDetail) => parseNumber(row.rate),
            sortable: true,
            sortField: 'rate',
            minWidth: '150px',
            cell: (row: TransportationQuoteDetail) => {
                const currencyLabel = getCurrencyLabel(row.currency);
                const normalizedCurrency = currencyLabel === '-' ? 'CLP' : currencyLabel;

                return formatMoney(parseNumber(row.rate), normalizedCurrency);
            }
        },
        {
            name: 'Unidad',
            selector: (row: TransportationQuoteDetail) => getCurrencyLabel(row.currency),
            sortable: true,
            sortField: 'unit',
            minWidth: '110px',
            cell: (row: TransportationQuoteDetail) => {
                const currencyLabel = getCurrencyLabel(row.currency);
                const rateUnit = row.rate_unit ?? '';
                if (!currencyLabel && !rateUnit) {
                    return '-';
                }
                if (!rateUnit) {
                    return currencyLabel;
                }
                if (!currencyLabel || currencyLabel === '-') {
                    return rateUnit;
                }
                return `${currencyLabel}/${rateUnit}`;
            }
        },
        {
            name: 'F. Despacho',
            selector: (row: TransportationQuoteDetail) => row.first_dispatch_date ?? '',
            sortable: true,
            sortField: 'first_dispatch_date',
            minWidth: '140px',
            cell: (row: TransportationQuoteDetail) => (
                <span className="">{momentParseDate(row.first_dispatch_date ?? '')}</span>
            )
        },
        {
            name: 'Acciones',
            selector: (row: TransportationQuoteDetail) => row.id ?? -1,
            sortable: false,
            width: '135px',
            cell: (row: TransportationQuoteDetail) => {
                const lockedDestroy = row.status === 'CONFIRMADA';
                return (
                    <div className="">
                        {show && (
                            <ButtonTableAction
                                callbackFunction={() => show(row.id ?? -1)}
                                classIcon={'mdi mdi-eye'}
                                colorIcon={'text-info'}
                                errorMessage={'No se puede mostrar este registro.'}
                                title={'Ver'}
                            />
                        )}
                        {edit && (
                            <ButtonTableAction
                                callbackFunction={() => edit(row.id ?? -1)}
                                classIcon={'mdi mdi-pencil'}
                                colorIcon={'text-warning'}
                                errorMessage={'No se puede editar este registro.'}
                                title={'Editar'}
                            />
                        )}
                        {destroy && (
                            <ButtonTableAction
                                callbackFunction={() => destroy(row.id ?? -1)}
                                locked={lockedDestroy}
                                classIcon={'mdi mdi-delete'}
                                colorIcon={'text-danger'}
                                errorMessage={'No se puede eliminar este registro.'}
                                title={'Eliminar'}
                            />
                        )}
                    </div>
                );
            }
        }
    ];

    return (
        <div className="row">
            <div className="col-12 mt-4 quote-details-datatable">
                <DefaultDataTable
                    columns={columns}
                    data={quoteDetails}
                    progressPending={loading}
                    filter={filter}
                    setFilter={setFilter}
                    paginationTotalRows={totalRows}
                />
            </div>
        </div>
    );
};

export default QuoteDetailsDataTable;
